import axios from 'axios';

import { captureApiException, requester } from '@/api/axios';

import { apiConst, keyConst } from '@/constants';

import {
  ApiResponse,
  ISendBulkCurrentProjectNda,
  ISendBulkCurrentProjectNdaResponse,
  ISendBulkEmailPayload,
  ISendBulkExpertEmailPayload,
  ISendCurrentProjectNdaMailPayload,
  ISendExpertEmailPayload,
} from '@/interface';

function getMailApiClientConfig() {
  const mailBaseUrl = import.meta.env.VITE_MAIL_BASE_URL || 'http://localhost:4011';
  const normalizedBaseUrl = mailBaseUrl.replace(/\/+$/, '');
  const backendToken = import.meta.env.VITE_MAIL_BACKEND_TOKEN;
  const userToken = localStorage.getItem(keyConst.userToken);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (backendToken) headers.Authorization = `Bearer ${backendToken}`;
  else if (userToken) headers.Authorization = `Bearer ${userToken}`;

  return { baseUrl: normalizedBaseUrl, headers };
}

export const sendBulkMails = async (data: ISendBulkEmailPayload): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.sendBulkMails,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const sendExpertMail = async (data: ISendExpertEmailPayload): Promise<ApiResponse<unknown>> => {
  const { baseUrl, headers } = getMailApiClientConfig();
  try {
    return await axios.post(`${baseUrl}/${apiConst.sendExpertMail}`, data, { headers });
  } catch (error) {
    captureApiException(error, 'mail.sendExpertMail', { baseUrl });
    throw error;
  }
};

export const sendBulkExpertMail = async (
  data: ISendBulkExpertEmailPayload,
): Promise<ApiResponse<{ sent?: number; failed?: number }>> => {
  const { baseUrl, headers } = getMailApiClientConfig();
  try {
    return await axios.post(`${baseUrl}/${apiConst.sendBulkExpertMail}`, data, { headers });
  } catch (error) {
    captureApiException(error, 'mail.sendBulkExpertMail', { baseUrl, itemCount: data?.items?.length });
    throw error;
  }
};

export const sendCurrentProjectNdaMail = async (data: ISendCurrentProjectNdaMailPayload): Promise<ApiResponse<unknown>> => {
  const { baseUrl, headers } = getMailApiClientConfig();
  try {
    return await axios.post(`${baseUrl}/${apiConst.sendCurrentProjectNdaMail}`, data, { headers });
  } catch (error) {
    captureApiException(error, 'mail.sendCurrentProjectNdaMail', { baseUrl });
    throw error;
  }
};

export const sendBulkCurrentProjectNdaMail = async (
  data: ISendBulkCurrentProjectNda,
): Promise<ApiResponse<ISendBulkCurrentProjectNdaResponse>> => {
  const { baseUrl, headers } = getMailApiClientConfig();
  try {
    return await axios.post(`${baseUrl}/${apiConst.sendBulkCurrentProjectNdaMail}`, data, { headers });
  } catch (error) {
    captureApiException(error, 'mail.sendBulkCurrentProjectNdaMail', { baseUrl, itemCount: data?.items?.length });
    throw error;
  }
};

export const declineScreeningInvite = async (token: string): Promise<ApiResponse<unknown>> => {
  const { baseUrl, headers } = getMailApiClientConfig();
  try {
    return await axios.get(`${baseUrl}/mail/screening/decline`, {
      headers,
      params: { t: token },
    });
  } catch (error) {
    captureApiException(error, 'mail.declineScreeningInvite', { baseUrl });
    throw error;
  }
};
